import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Modal,
  ActivityIndicator,
  Dimensions,
  Alert,
} from 'react-native';
import { Image } from 'expo-image';
import * as ImagePicker from 'expo-image-picker';
import { useTheme } from '../context/ThemeContext';
import CourseMediaCarousel, { createCourseMediaCarouselStyles } from './CourseMediaCarousel';
import { getSupabaseClient, getSupabaseConfigHint } from '../utils/supabase';
import { listStudentGalleryImages, uploadStudentGalleryImage } from '../utils/studentGallery';

const GALLERY_COLS = 4;
const GRID_GAP = 8;

function guessMimeFromName(name) {
  const lower = String(name).toLowerCase();
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.gif')) return 'image/gif';
  if (lower.endsWith('.heic')) return 'image/heic';
  if (lower.endsWith('.jpeg')) return 'image/jpeg';
  return 'image/jpeg';
}

async function uriToArrayBuffer(uri) {
  const res = await fetch(uri);
  if (!res.ok) throw new Error(`Could not read image (${res.status})`);
  return res.arrayBuffer();
}

export default function StudentGallerySection({ studentName, isOwnProfile, sectionWidth }) {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [images, setImages] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(true);
  const [galleryError, setGalleryError] = useState('');
  const [pendingUpload, setPendingUpload] = useState(null);
  const [uploadSaving, setUploadSaving] = useState(false);
  const [uploadJustSaved, setUploadJustSaved] = useState(false);
  const [toast, setToast] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalStartIndex, setModalStartIndex] = useState(0);

  const displayName = String(studentName || '').trim() || 'student';
  const contentWidth = sectionWidth || Dimensions.get('window').width * 0.8;
  const tileSize = Math.floor(
    (contentWidth - GRID_GAP * (GALLERY_COLS - 1)) / GALLERY_COLS
  );

  const carouselSlideWidth = Math.min(contentWidth - 32, 520);
  const carouselSlideHeight = Math.round(carouselSlideWidth * 0.75);
  const carouselStyles = useMemo(
    () => createCourseMediaCarouselStyles(theme, carouselSlideWidth, carouselSlideHeight),
    [theme, carouselSlideWidth, carouselSlideHeight]
  );

  const mediaUrls = useMemo(() => images.map((img) => img.url), [images]);

  const loadGallery = useCallback(async () => {
    setGalleryError('');
    setLoadingGallery(true);
    try {
      const supabase = getSupabaseClient();
      if (!supabase) {
        setImages([]);
        setGalleryError('Gallery unavailable (Supabase not configured).');
        return;
      }
      const rows = await listStudentGalleryImages(supabase, displayName);
      setImages(rows);
    } catch (error) {
      console.error('Error loading student gallery:', error);
      setGalleryError(error?.message || 'Could not load gallery');
      setImages([]);
    } finally {
      setLoadingGallery(false);
    }
  }, [displayName]);

  useEffect(() => {
    void loadGallery();
  }, [loadGallery]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const handlePickImage = async () => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permission needed', 'Allow photo library access to upload gallery images.');
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: false,
        quality: 0.92,
      });
      if (!result.canceled && result.assets?.[0]) {
        const asset = result.assets[0];
        const fileName = asset.fileName || asset.uri?.split('/').pop() || 'gallery-image.jpg';
        setPendingUpload({ uri: asset.uri, fileName });
        setUploadJustSaved(false);
      }
    } catch (error) {
      console.error('Error picking gallery image:', error);
      Alert.alert('Error', 'Failed to pick image');
    }
  };

  const handleSaveUpload = async () => {
    if (!pendingUpload || !isOwnProfile) return;
    const supabase = getSupabaseClient();
    if (!supabase) {
      Alert.alert('Upload unavailable', `Configure Supabase:\n${getSupabaseConfigHint().join(', ')}`);
      return;
    }
    setUploadSaving(true);
    try {
      await uploadStudentGalleryImage(supabase, displayName, {
        uri: pendingUpload.uri,
        fileName: pendingUpload.fileName,
        uriToArrayBuffer,
        guessMimeFromName,
      });
      setPendingUpload(null);
      setUploadJustSaved(true);
      setToast({ type: 'success', message: 'Gallery image saved successfully.' });
      await loadGallery();
    } catch (error) {
      console.error('Error saving gallery image:', error);
      setToast({ type: 'error', message: error?.message || 'Failed to save gallery image' });
    } finally {
      setUploadSaving(false);
    }
  };

  const openGalleryModal = (index) => {
    setModalStartIndex(index);
    setModalVisible(true);
  };

  return (
    <View
      style={styles.section}
      {...(Platform.OS === 'web' ? { dataSet: { profileStudentGallerySection: 'true' } } : {})}
    >
      <View style={styles.sectionDivider} />
      <Text style={styles.sectionTitle}>Student Gallery</Text>

      {loadingGallery ? (
        <ActivityIndicator
          style={styles.loader}
          color={theme.colors.primary || '#d4af37'}
        />
      ) : galleryError ? (
        <Text style={styles.errorText}>{galleryError}</Text>
      ) : images.length === 0 ? (
        <Text style={styles.emptyText}>No gallery images yet.</Text>
      ) : (
        <View style={styles.grid}>
          {images.map((item, index) => (
            <TouchableOpacity
              key={item.name}
              style={[styles.tile, { width: tileSize, height: tileSize }]}
              onPress={() => openGalleryModal(index)}
              activeOpacity={0.85}
              {...(Platform.OS === 'web'
                ? { dataSet: { profileStudentGalleryTile: 'true' } }
                : {})}
            >
              <Image source={{ uri: item.url }} style={styles.tileImage} contentFit="cover" />
            </TouchableOpacity>
          ))}
        </View>
      )}

      {isOwnProfile ? (
        <View style={styles.uploadBlock}>
          {toast ? (
            <View
              style={[
                styles.toast,
                toast.type === 'error' ? styles.toastError : styles.toastSuccess,
              ]}
              {...(Platform.OS === 'web'
                ? {
                    dataSet: {
                      profileStudentGalleryToast: 'true',
                      profileGalleryToastType: toast.type,
                    },
                  }
                : {})}
            >
              <Text style={styles.toastMessage}>{toast.message}</Text>
              <TouchableOpacity
                onPress={() => setToast(null)}
                style={styles.toastClose}
                accessibilityLabel="Dismiss"
              >
                <Text style={styles.toastCloseText}>✕</Text>
              </TouchableOpacity>
            </View>
          ) : null}

          <View style={styles.uploadActionsRow}>
            <View style={styles.uploadLeftColumn}>
              <TouchableOpacity
                style={styles.uploadButton}
                onPress={() => void handlePickImage()}
                activeOpacity={0.8}
                disabled={uploadSaving}
                {...(Platform.OS === 'web'
                  ? { dataSet: { profileStudentGalleryUploadBtn: 'true' } }
                  : {})}
              >
                <Text style={styles.uploadButtonText}>Upload Gallery Image</Text>
              </TouchableOpacity>
              {pendingUpload ? (
                <View style={styles.pendingFileRow}>
                  <Text style={styles.pendingFileName} numberOfLines={2}>
                    {pendingUpload.fileName}
                  </Text>
                </View>
              ) : null}
            </View>
            <TouchableOpacity
              style={[
                styles.saveButtonSide,
                !pendingUpload && !uploadJustSaved && styles.saveButtonSideMuted,
                pendingUpload && !uploadSaving && styles.saveButtonSideReady,
                uploadJustSaved &&
                  !pendingUpload &&
                  !uploadSaving &&
                  styles.saveButtonSideSuccess,
              ]}
              onPress={() => void handleSaveUpload()}
              disabled={uploadSaving || !pendingUpload}
              activeOpacity={0.8}
              {...(Platform.OS === 'web'
                ? { dataSet: { profileStudentGallerySaveBtn: 'true' } }
                : {})}
            >
              {uploadSaving ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text
                  style={[
                    styles.saveButtonSideText,
                    !pendingUpload && !uploadJustSaved && styles.saveButtonSideTextMuted,
                  ]}
                >
                  Save
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      ) : null}

      <Modal
        visible={modalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setModalVisible(false)}
      >
        <View
          style={styles.modalOverlay}
          {...(Platform.OS === 'web' ? { dataSet: { profileStudentGalleryModal: 'true' } } : {})}
        >
          <TouchableOpacity
            style={styles.modalBackdrop}
            activeOpacity={1}
            onPress={() => setModalVisible(false)}
          />
          <View style={styles.modalSheet} onStartShouldSetResponder={() => true}>
            <TouchableOpacity
              style={styles.modalCloseBtn}
              onPress={() => setModalVisible(false)}
              accessibilityLabel="Close gallery"
            >
              <Text style={styles.modalCloseText}>✕</Text>
            </TouchableOpacity>
            {mediaUrls.length > 0 ? (
              <CourseMediaCarousel
                mediaUrls={mediaUrls}
                slideWidth={carouselSlideWidth}
                slideHeight={carouselSlideHeight}
                theme={theme}
                styles={carouselStyles}
                initialIndex={modalStartIndex}
                pauseAutoRotate
              />
            ) : null}
          </View>
        </View>
      </Modal>
    </View>
  );
}

function getStyles(theme) {
  return StyleSheet.create({
    section: {
      width: '100%',
      alignSelf: 'stretch',
      marginTop: 28,
      backgroundColor: 'transparent',
    },
    sectionDivider: {
      height: 2,
      width: '100%',
      backgroundColor: theme.colors.border,
      marginBottom: 16,
      opacity: 0.65,
    },
    sectionTitle: {
      fontSize: 20,
      fontWeight: '700',
      color: theme.colors.heading,
      marginBottom: 16,
      textAlign: 'center',
      backgroundColor: 'transparent',
    },
    loader: {
      marginVertical: 20,
    },
    errorText: {
      color: '#c62828',
      fontSize: 14,
      textAlign: 'center',
      marginBottom: 12,
      backgroundColor: 'transparent',
    },
    emptyText: {
      color: theme.colors.text,
      fontSize: 14,
      textAlign: 'center',
      opacity: 0.65,
      marginBottom: 12,
      backgroundColor: 'transparent',
    },
    grid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: GRID_GAP,
      marginBottom: 16,
      backgroundColor: 'transparent',
    },
    tile: {
      borderRadius: 6,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#f0f0f0',
    },
    tileImage: {
      width: '100%',
      height: '100%',
    },
    uploadBlock: {
      width: '100%',
      marginTop: 4,
      backgroundColor: 'transparent',
    },
    uploadActionsRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      width: '100%',
      backgroundColor: 'transparent',
    },
    uploadLeftColumn: {
      flex: 1,
      marginRight: 10,
      backgroundColor: 'transparent',
    },
    uploadButton: {
      width: '100%',
      backgroundColor: theme.colors.primary || '#d4af37',
      paddingVertical: 14,
      paddingHorizontal: 12,
      borderRadius: 8,
      justifyContent: 'center',
      alignItems: 'center',
    },
    uploadButtonText: {
      color: '#FFFFFF',
      fontSize: 15,
      fontWeight: '600',
      textAlign: 'center',
      backgroundColor: 'transparent',
    },
    pendingFileRow: {
      marginTop: 8,
      backgroundColor: 'transparent',
    },
    pendingFileName: {
      fontSize: 13,
      color: theme.colors.text,
      opacity: 0.8,
      backgroundColor: 'transparent',
    },
    saveButtonSide: {
      minWidth: 96,
      paddingVertical: 14,
      paddingHorizontal: 16,
      borderRadius: 8,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#999999',
    },
    saveButtonSideMuted: {
      opacity: 0.55,
    },
    saveButtonSideReady: {
      backgroundColor: theme.colors.primary || '#d4af37',
      opacity: 1,
    },
    saveButtonSideSuccess: {
      backgroundColor: '#22c55e',
      opacity: 1,
    },
    saveButtonSideText: {
      color: '#FFFFFF',
      fontSize: 16,
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    saveButtonSideTextMuted: {
      color: '#E8E8E8',
    },
    toast: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingVertical: 12,
      paddingHorizontal: 12,
      borderRadius: 8,
      marginBottom: 12,
    },
    toastSuccess: {
      backgroundColor: '#2e7d32',
    },
    toastError: {
      backgroundColor: '#c62828',
    },
    toastMessage: {
      flex: 1,
      color: '#ffffff',
      fontSize: 15,
      lineHeight: 20,
      marginRight: 8,
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    toastClose: {
      padding: 4,
      marginTop: -2,
    },
    toastCloseText: {
      color: '#ffffff',
      fontSize: 18,
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    modalOverlay: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
    },
    modalBackdrop: {
      ...StyleSheet.absoluteFillObject,
    },
    modalSheet: {
      width: '92%',
      maxWidth: 560,
      paddingTop: 36,
      paddingBottom: 16,
      paddingHorizontal: 8,
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#111',
      borderRadius: 12,
    },
    modalCloseBtn: {
      position: 'absolute',
      top: 8,
      right: 8,
      zIndex: 10,
      width: 36,
      height: 36,
      borderRadius: 18,
      backgroundColor: 'rgba(255,255,255,0.15)',
      alignItems: 'center',
      justifyContent: 'center',
    },
    modalCloseText: {
      color: '#fff',
      fontSize: 20,
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
  });
}
